<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { resolveProductsOrdersFeatures } from 'dashboard/helper/productsOrdersFeatures';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CreateOrderQuickDialog from 'dashboard/components/widgets/conversation/CreateOrderQuickDialog.vue';
import ProductOrderDetailDialog from 'dashboard/routes/dashboard/productsOrders/components/ProductOrderDetailDialog.vue';

const props = defineProps({
  contact: {
    type: Object,
    default: () => ({}),
  },
  currentChat: {
    type: Object,
    default: () => ({}),
  },
});

const store = useStore();
const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const detailDialogRef = ref(null);
const quickCreateDialogRef = ref(null);

const productOrders = useMapGetter('productsOrders/getProductOrders');
const uiFlags = useMapGetter('productsOrders/getUIFlags');

const currentAccount = computed(() =>
  store.getters['accounts/getAccount'](Number(route.params.accountId))
);

const productsOrdersFeatures = computed(() =>
  resolveProductsOrdersFeatures(currentAccount.value?.settings)
);

const contactOrders = computed(() => {
  if (!props.contact?.id) {
    return [];
  }

  return productOrders.value.filter(order => {
    const sameContactId =
      Number(order.contactId) === Number(props.contact.id) ||
      Number(order.contact?.id) === Number(props.contact.id);
    const samePhone =
      props.contact.phone_number &&
      order.contactPhone &&
      order.contactPhone === props.contact.phone_number;

    return sameContactId || samePhone;
  });
});

const visibleOrders = computed(() => contactOrders.value.slice(0, 5));

const isLoading = computed(
  () => uiFlags.value.isFetchingProductOrders && !contactOrders.value.length
);

const summary = computed(() => {
  const pending = contactOrders.value.filter(order => order.status === 'pending').length;
  const active = contactOrders.value.filter(order =>
    ['confirmed', 'preparing', 'shipped', 'in_progress'].includes(order.status)
  ).length;
  const completed = contactOrders.value.filter(order =>
    ['completed', 'delivered'].includes(order.status)
  ).length;

  return [
    {
      key: 'pending',
      label: t('PRODUCTS_ORDERS.SUMMARY.PENDING'),
      value: pending,
    },
    {
      key: 'active',
      label: t('PRODUCTS_ORDERS.SUMMARY.IN_PROGRESS'),
      value: active,
    },
    {
      key: 'completed',
      label: t(
        productsOrdersFeatures.value.advancedStatuses
          ? 'PRODUCTS_ORDERS.SUMMARY.DELIVERED'
          : 'PRODUCTS_ORDERS.SUMMARY.COMPLETED'
      ),
      value: completed,
    },
  ];
});

const ensureOrdersLoaded = () => {
  store.dispatch('productsOrders/getProductOrders');
};

const openOrderDetails = order => {
  detailDialogRef.value?.open?.(order);
};

const openOrdersModule = () => {
  router.push({
    name: 'products_orders_index',
    params: { accountId: route.params.accountId },
  });
};

const openCreateOrder = () => {
  if (
    productsOrdersFeatures.value.quickCreateFromConversation &&
    props.currentChat?.id
  ) {
    quickCreateDialogRef.value?.open?.();
    return;
  }

  router.push({
    name: 'products_orders_index',
    params: { accountId: route.params.accountId },
    query: props.currentChat?.id
      ? {
          openOrder: 'conversation',
          conversationId: props.currentChat.id,
        }
      : undefined,
  });
};

const advanceOrder = async order => {
  try {
    await store.dispatch('productsOrders/advanceProductOrder', order.id);
    useAlert(t('PRODUCTS_ORDERS.ALERTS.ORDER_ADVANCED'));
  } catch (error) {
    // Store toast handles API errors
  }
};

const completeOrder = async order => {
  try {
    await store.dispatch('productsOrders/completeProductOrder', order.id);
    useAlert(t('PRODUCTS_ORDERS.ALERTS.ORDER_COMPLETED'));
  } catch (error) {
    // Store toast handles API errors
  }
};

const cancelOrder = async order => {
  try {
    await store.dispatch('productsOrders/cancelProductOrder', order.id);
    useAlert(t('PRODUCTS_ORDERS.ALERTS.ORDER_CANCELED'));
  } catch (error) {
    // Store toast handles API errors
  }
};

const statusBadgeClass = status => {
  const classes = {
    pending:
      'border-n-amber-6/40 bg-n-amber-3 text-n-amber-11 dark:border-n-amber-9/40 dark:bg-n-amber-3/10 dark:text-n-amber-11',
    confirmed:
      'border-n-blue-6/40 bg-n-blue-3 text-n-blue-11 dark:border-n-blue-9/40 dark:bg-n-blue-3/10 dark:text-n-blue-11',
    preparing:
      'border-n-indigo-6/40 bg-n-indigo-3 text-n-indigo-11 dark:border-n-indigo-9/40 dark:bg-n-indigo-3/10 dark:text-n-indigo-11',
    shipped:
      'border-n-cyan-6/40 bg-n-cyan-3 text-n-cyan-11 dark:border-n-cyan-9/40 dark:bg-n-cyan-3/10 dark:text-n-cyan-11',
    delivered:
      'border-n-teal-6/40 bg-n-teal-3 text-n-teal-11 dark:border-n-teal-9/40 dark:bg-n-teal-3/10 dark:text-n-teal-11',
    in_progress:
      'border-n-blue-6/40 bg-n-brand/10 text-n-blue-11 dark:border-n-blue-9/40 dark:bg-n-brand/10 dark:text-n-blue-11',
    completed:
      'border-n-teal-6/40 bg-n-teal-3 text-n-teal-11 dark:border-n-teal-9/40 dark:bg-n-teal-3/10 dark:text-n-teal-11',
    canceled:
      'border-n-ruby-6/40 bg-n-ruby-3 text-n-ruby-11 dark:border-n-ruby-9/40 dark:bg-n-ruby-3/10 dark:text-n-ruby-11',
  };

  return classes[status] || classes.pending;
};

const statusLabel = status =>
  t(`PRODUCTS_ORDERS.STATUS.${status.toUpperCase()}`);

watch(
  () => props.contact?.id,
  contactId => {
    if (contactId) {
      ensureOrdersLoaded();
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-3 p-1">
    <div
      v-if="!contact?.id"
      class="rounded-lg border border-dashed border-n-weak px-3 py-6 text-center text-sm text-n-slate-10"
    >
      {{ $t('CONVERSATION_SIDEBAR.PRODUCT_ORDERS.NO_CONTACT') }}
    </div>

    <template v-else>
      <div class="flex flex-wrap items-center justify-between gap-2">
        <p class="mb-0 text-sm text-n-slate-11">
          {{ $t('CONVERSATION_SIDEBAR.PRODUCT_ORDERS.DESCRIPTION') }}
        </p>
        <div class="flex flex-wrap gap-2">
          <Button
            variant="ghost"
            color="slate"
            sm
            icon="i-lucide-external-link"
            :label="$t('PRODUCTS_ORDERS.BUTTONS.VIEW_MODULE')"
            @click="openOrdersModule"
          />
          <Button
            sm
            icon="i-lucide-plus"
            :label="$t('PRODUCTS_ORDERS.BUTTONS.CREATE_FROM_CONVERSATION')"
            @click="openCreateOrder"
          />
        </div>
      </div>

      <div class="grid gap-2 md:grid-cols-3">
        <div
          v-for="item in summary"
          :key="item.key"
          class="rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2"
        >
          <p class="mb-1 text-xs uppercase tracking-[0.08em] text-n-slate-10">
            {{ item.label }}
          </p>
          <p class="mb-0 text-base font-semibold text-n-slate-12">
            {{ item.value }}
          </p>
        </div>
      </div>

      <div
        v-if="isLoading"
        class="flex min-h-[8rem] items-center justify-center"
      >
        <Spinner class="text-n-brand" />
      </div>

      <div
        v-else-if="!contactOrders.length"
        class="rounded-lg border border-dashed border-n-weak px-3 py-6 text-center text-sm text-n-slate-10"
      >
        {{ $t('CONVERSATION_SIDEBAR.PRODUCT_ORDERS.EMPTY') }}
      </div>

      <div v-else class="flex flex-col gap-3">
        <button
          v-for="order in visibleOrders"
          :key="order.id"
          type="button"
          class="w-full rounded-xl border border-n-weak bg-n-surface-1 p-3 text-left transition hover:border-n-slate-6"
          @click="openOrderDetails(order)"
        >
          <div class="flex flex-wrap items-center justify-between gap-2">
            <p class="mb-0 text-sm font-semibold text-n-slate-12">
              {{ order.reference }}
            </p>
            <span
              class="inline-flex rounded-full border px-2 py-1 text-xs font-semibold"
              :class="statusBadgeClass(order.status)"
            >
              {{ statusLabel(order.status) }}
            </span>
          </div>
          <p class="mt-2 mb-0 text-sm text-n-slate-12">
            {{
              order.product?.name ||
              $t('PRODUCTS_ORDERS.LABELS.PRODUCT_NOT_SELECTED')
            }}
          </p>
          <div class="mt-2 flex flex-wrap items-center gap-2 text-xs text-n-slate-10">
            <span class="inline-flex items-center gap-1">
              <Icon icon="i-lucide-package-2" class="size-3.5" />
              {{
                $t('PRODUCTS_ORDERS.LABELS.QUANTITY', {
                  count: order.quantity,
                })
              }}
            </span>
            <span class="inline-flex items-center gap-1">
              <Icon icon="i-lucide-user-round" class="size-3.5" />
              {{ order.contactName }}
            </span>
          </div>
        </button>

        <p
          v-if="contactOrders.length > visibleOrders.length"
          class="mb-0 text-xs text-n-slate-10"
        >
          {{
            $t('CONVERSATION_SIDEBAR.PRODUCT_ORDERS.SHOWING', {
              shown: visibleOrders.length,
              total: contactOrders.length,
            })
          }}
        </p>
      </div>
    </template>

    <CreateOrderQuickDialog
      ref="quickCreateDialogRef"
      :current-chat="currentChat"
    />
    <ProductOrderDetailDialog
      ref="detailDialogRef"
      :orders="productOrders"
      :products-orders-features="productsOrdersFeatures"
      :show-edit-action="false"
      @advance="advanceOrder"
      @cancel="cancelOrder"
      @complete="completeOrder"
    />
  </div>
</template>
